/**
 * @file
 * Main JavaScript entry point for Versa theme.
 * Uses lazy loading for GSAP to improve initial page load performance.
 */

(function (Drupal, once) {
  'use strict';

  /**
   * Initialize page loader.
   */
  Drupal.behaviors.versaPageLoader = {
    attach(context) {
      once('versa-page-loader', 'body', context).forEach(async () => {
        try {
          const { default: PageLoader } = await import('./components/page-loader.js');
          const loader = new PageLoader();
          loader.init();
        } catch (error) {
          // Error loading page loader
        }
      });
    },
  };

  /**
   * Initialize site navigation toggle with GSAP.
   * Navigation now requires GSAP for Timeline animations.
   */
  Drupal.behaviors.versaNavigation = {
    attach(context) {
      once('versa-navigation', 'body', context).forEach(async () => {
        const navToggle = document.querySelector('[data-nav-toggle]');
        if (!navToggle) {
          return;
        }

        try {
          // Load GSAP for navigation animations
          const { gsap } = await import('gsap');
          const { default: Navigation } = await import('./components/navigation.js');
          new Navigation(gsap);
        } catch (error) {
          // Error loading navigation
        }
      });
    },
  };

  /**
   * Initialize GSAP animations.
   */
  Drupal.behaviors.versaAnimations = {
    attach(context) {
      once('versa-animations', 'body', context).forEach(async () => {
        // Check for elements that use GSAP
        const header = document.querySelector('.site-header');
        const projectsMarquee = document.querySelector('[data-projects-marquee]');
        const animatedTextElement = document.querySelector('[data-animated-text]');
        const sitesGallery = document.querySelector('[data-sites-gallery]');
        const workView = document.querySelector('.view-work, .views-view--work');
        const projectPage = document.querySelector('[data-project-page]');
        const logoMarquee = document.querySelector('.logo-marquee');

        // Only load GSAP if animation elements exist
        const animatedElements = document.querySelectorAll('.hero, [data-animate]');

        if (animatedElements.length === 0 && !header && !projectsMarquee && !animatedTextElement && !sitesGallery && !workView && !projectPage && !logoMarquee) {
          return;
        }

        try {
          // Lazy load GSAP modules
          const [{ gsap }, { ScrollTrigger }] = await Promise.all([
            import('gsap'),
            import('gsap/ScrollTrigger'),
          ]);

          // Make GSAP globally available for utility functions
          window.gsap = gsap;

          // Register plugins
          gsap.registerPlugin(ScrollTrigger);

          // Ensure ScrollTrigger is accessible on gsap object
          gsap.ScrollTrigger = ScrollTrigger;

          // Load and initialize header scroll behavior (present on all pages)
          const header = document.querySelector('.site-header');
          if (header) {
            const { default: Header } = await import('./components/header.js');
            new Header(gsap, ScrollTrigger);
          }

          // Load and initialize global cursor (present on all pages)
          const { default: GlobalCursor } = await import('./components/global-cursor.js');
          new GlobalCursor(gsap);

          // Load and initialize hero animations
          if (document.querySelector('.hero')) {
            const { default: heroAnimation } = await import('./components/hero.js');
            heroAnimation(gsap, ScrollTrigger);
          }

          // Load and initialize projects marquee
          if (projectsMarquee) {
            // Load Draggable plugin
            const { Draggable } = await import('gsap/Draggable');

            // Load marquee component
            const { default: ProjectsMarquee } = await import('./components/projects-marquee.js');
            new ProjectsMarquee(gsap, Draggable);
          }

          // Load and initialize animated text
          const animatedText = document.querySelector('[data-animated-text]');
          if (animatedText) {
            const { default: AnimatedText } = await import('./components/animated-text.js');
            new AnimatedText(gsap);
          }

          // Load and initialize sites gallery
          if (sitesGallery) {
            const { default: SitesGallery } = await import('./components/sites-gallery.js');
            new SitesGallery(gsap);
          }

          // Load and initialize work page animations
          if (workView) {
            const { default: WorkAnimations } = await import('./components/work-animations.js');
            new WorkAnimations(gsap);
          }

          // Load and initialize project page animations
          if (projectPage) {
            const { default: ProjectPage } = await import('./components/project-page.js');
            new ProjectPage(gsap);
          }

          // Load and initialize logo marquee
          if (logoMarquee) {
            const { default: LogoMarquee } = await import('./components/logo-marquee.js');
            new LogoMarquee(gsap);
          }

          // Initialize animations for elements with data-animate attribute
          animatedElements.forEach((element) => {
            if (element.dataset.animate) {
              const animationType = element.dataset.animate;
              initializeAnimation(element, animationType, gsap, ScrollTrigger);
            }
          });

          // Refresh ScrollTrigger after images load
          window.addEventListener('load', () => {
            ScrollTrigger.refresh();
          });
        } catch (error) {
          // Error loading GSAP animations
        }
      });
    },
  };

  /**
   * Initialize animation based on type.
   */
  function initializeAnimation(element, type, gsap, ScrollTrigger) {
    const animations = {
      'fade-in': () => {
        gsap.from(element, {
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
          opacity: 0,
          y: 30,
          duration: 0.8,
        });
      },
      'slide-in-left': () => {
        gsap.from(element, {
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
          opacity: 0,
          x: -50,
          duration: 0.8,
        });
      },
      'slide-in-right': () => {
        gsap.from(element, {
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
          opacity: 0,
          x: 50,
          duration: 0.8,
        });
      },
    };

    const animation = animations[type];
    if (animation) {
      animation();
    }
  }
})(Drupal, once);
